import {
  AdminAccessSchema,
  type AdminAccess,
  AuthorizePinBodySchema,
  type CreatedRaid,
  CreatedRaidSchema,
  type Favorites, FavoritesSchema,
  type MelangeDistributionResult,
  MelangeDistributionResultSchema,
  type MelangePreviewRequest,
  MelangePreviewRequestSchema,
  type Raid,
  type Raids,
  RaidSchema,
  RaidsSchema,
} from "./RaidSchema.ts";
import { get, patch, post } from "@/api/api.ts";
import {
  RAIDS,
  RAIDS_FAVORITES,
  RAID_ADMIN_PIN,
  RAID_AUTHORISATION,
  RAID_BY_ID,
  RAID_START,
  RAID_CLOSE,
  RAID_MELANGE_PREVIEW,
  RAID_TIME_TRACK_START,
  RAID_TIME_TRACK_STOP,
  PARTICIPANT_MARK_RESOURCES_RETRIEVED,
} from "@/api/Pathes.ts";
import { buildUrl } from "@/api/urlHelpers.ts";
import { sleep } from "@/helpers/Sleeper.ts";
import { z } from "zod";

async function getRaids(signal?: AbortSignal): Promise<Raids> {
  await sleep(1000);
  return await get(RAIDS, RaidsSchema, { signal });
}

async function getFavorites(signal?: AbortSignal): Promise<Favorites> {
  await sleep(1000);
  return await get(RAIDS_FAVORITES, FavoritesSchema, { signal });
}

async function createRaid(name: string): Promise<CreatedRaid> {
  const encodedName = encodeURIComponent(name);
  return await post(RAIDS + "?name=" + encodedName, undefined, CreatedRaidSchema);
}

async function activateAdmin(raidId: number, pin: string): Promise<AdminAccess> {
  return await post(
    buildUrl(RAID_AUTHORISATION, { raidId: raidId.toString() }),
    { pin },
    AdminAccessSchema,
    { bodySchema: AuthorizePinBodySchema },
  );
}

async function recoverAdminPin(raidId: number): Promise<AdminAccess> {
  return await get(
    buildUrl(RAID_ADMIN_PIN, { raidId: raidId.toString() }),
    AdminAccessSchema,
  );
}

async function getRaid(id: number, signal?: AbortSignal): Promise<Raid> {
  return await get(buildUrl(RAID_BY_ID, { raidId: id.toString() }), RaidSchema, { signal });
}

async function updateRaid(id: number, raid: Partial<Raid>): Promise<Raid> {
  return await patch(buildUrl(RAID_BY_ID, { raidId: id.toString() }), raid, RaidSchema);
}

async function startRaid(id: number, raid: Raid): Promise<Raid> {
  return await post(buildUrl(RAID_START, { raidId: id.toString() }), raid, RaidSchema);
}

async function closeRaid(id: number, raid: Raid): Promise<Raid> {
  return await post(buildUrl(RAID_CLOSE, { raidId: id.toString() }), raid, RaidSchema);
}

async function postMelangePreview(
  raidId: number,
  body: MelangePreviewRequest,
): Promise<MelangeDistributionResult> {
  return await post(
    buildUrl(RAID_MELANGE_PREVIEW, { raidId: raidId.toString() }),
    body,
    MelangeDistributionResultSchema,
    { bodySchema: MelangePreviewRequestSchema },
  );
}

async function stopTimeTracking(raidId: number, participantId: number): Promise<Raid> {
  return await post(
    buildUrl(RAID_TIME_TRACK_STOP, { raidId: raidId.toString(), participantId: participantId.toString() }),
    null,
    RaidSchema,
  );
}

async function startTimeTracking(raidId: number, participantId: number): Promise<Raid> {
  return await post(
    buildUrl(RAID_TIME_TRACK_START, { raidId: raidId.toString(), participantId: participantId.toString() }),
    null,
    RaidSchema,
  );
}

async function markResourceReceived(participantId: number): Promise<undefined> {
  return await post(
    buildUrl(PARTICIPANT_MARK_RESOURCES_RETRIEVED, { participantId: participantId.toString() }),
    null,
    z.undefined(),
  );
}

export {
  getRaids,
  getRaid,
  createRaid,
  activateAdmin,
  recoverAdminPin,
  updateRaid,
  startRaid,
  closeRaid,
  stopTimeTracking,
  startTimeTracking,
  getFavorites,
  markResourceReceived,
  postMelangePreview,
};
